import { useToastCountManager } from '@design-system/index'
import { observer } from 'mobx-react'
import { lazy, Suspense, useEffect } from 'react'
import { matchPath, Redirect, useHistory } from 'react-router'
import { Switch } from 'react-router-dom'
import { GlobalStyle } from 'src/styles'
import { envUtils } from 'src/utils/envUtils'

import PermissionGuardRoute from '@/router/PermissionGuardRoute'
import { useAppLastActiveTimeoutEffect } from '@/utils'

import DefaultPageTemplate from './component/templates/PageTemplate/DefaultPageTemplate'
import PageContentTemplate from './component/templates/PageTemplate/PageContentTemplate'
import { ErrorBoundary, ErrorBoundaryFallback } from './ErrorBoundary'
import { useAppUpdateDetection } from './hooks/useAppUpdateDetection'
import { useBanner } from './hooks/useBanner'
import { useDevToolbar } from './hooks/useDevToolbar'
import { useInspectWorkbooks } from './hooks/useInspectWorkbooks'
import { useLifeCycle } from './hooks/useLifeCycle'
import { useDataTracking } from './hooks/useMixpanel'
import useSSE from './hooks/useSSE'
import { usePopup } from './hooks/usePopup'
import { useQuickMenuSetting } from './hooks/useQuickMenuSetting'
import { useSavePath } from './hooks/useSavePath'
import { useSetStudentGroups } from './hooks/useSetStudentGroups'

const GlobalFadeToast = lazy(() => import('./component/atoms/GlobalFadeToast/GlobalFadeToast'))
const GlobalLoader = lazy(() => import('./component/atoms/GlobalLoader/GlobalLoader'))
const FloatingActionButtonWrap = lazy(
  () => import('./contentComponents/FloatingActionButton/FloatingActionButtonWrap'),
)
const ScreenSizeGuide = lazy(() => import('./contentComponents/global/ScreenSizeGuide'))

// import { useSetVwoUserContext } from './hooks/useSetVwoUserContext'
import { useVirtualStudent } from './hooks/useVirtualStudent'
import ServerMaintenanceService from './page/etc/ServerMaintenance/_service/ServerMaintenance.service'
import commonRepo from './repository/common.repo'
import { RouteName, SCREEN_SIZE_GUIDE_EXCLUDE_ROUTES } from './router'
import { localStorageService } from './service/storage.service'

const Login = envUtils.isSchool
  ? lazy(() => import('./page/etc/Login/Login.school'))
  : lazy(() => import('./page/etc/Login/Login'))
const JeonnamLogin = lazy(() => import('./page/etc/B2GLogin/JeonnamLogin/JeonnamLogin'))
const PublicLogin = lazy(() => import('./page/etc/B2GLogin/PublicLogin/PublicLogin'))
const AiOnOAuthLogin = lazy(() => import('./page/etc/B2GLogin/AiOnLogin/AiOnOAuthLogin.page'))
const AiOnOAuthCallbackPage = lazy(
  () => import('./page/etc/B2GLogin/AiOnLogin/AiOnOAuthCallback.page'),
)
const AiepBridgePage = lazy(() => import('./page/etc/B2GLogin/AiepLogin/AiepBridge.page'))
const WeizmannLoginPage = lazy(() => import('./page/etc/Login/WeizmannLogin/WeizmannLogin.page'))
const OnlineOnboardingLoginPage = lazy(
  () => import('./page/etc/Login/OnlineOnboardingBridge/OnlineOnboardingLogin.page'),
)
const Lesson = lazy(() => import('./page/Lesson'))
const FindPassword = lazy(() => import('./page/etc/Login/FindPassword/FindPassword'))
const Academy = lazy(() => import('./page/Academy'))
const LessonPreparation = lazy(() => import('./page/Lesson-prepare/LessonPreparePage'))
const MyPage = lazy(() => import('./page/etc/MyPage'))
const MainPage = lazy(() => import('./page/etc/Main/Main.page'))
const MakeWorksheet = lazy(() => import('./page/MakeWorksheet'))
const WorksheetFileUpload = lazy(() => import('./page/WorksheetFileUpload'))
const MakeCustomWorkbook = lazy(() => import('./page/MakeCustomWorkbook/MakeCustomWorkbook'))
const MakeCas = lazy(() => import('./page/MakeCas/MakeCas.page'))
const MakeSignatureWorkbook = lazy(
  () => import('./page/MakeSignatureWorkbook/MakeSignatureWorkbook'),
)
const SupportWorkbook = lazy(
  () => import('./page/etc/MyPage/Customer/SupportWorkbookList/SupportWorkbook.page'),
)
const 추가요금내역 = lazy(() => import('./page/etc/MyPage/MyStatus/추가요금내역'))
const 문자요금내역 = lazy(() => import('./page/etc/MyPage/MyStatus/문자요금내역'))
const Hwp다운로드내역 = lazy(
  () => import('./page/etc/MyPage/MyStatus/hwp다운로드내역/hwp다운로드내역'),
)
const CreateUpdateClassManage = lazy(
  () => import('./page/Academy/AcademyManage/ClassManage/CreateUpdateClass'),
)
const WorksheetGroupScoring = lazy(() => import('./page/Lesson/Worksheet/WorksheetGroupScoring'))
const WorksheetDerivation = lazy(() => import('./page/Lesson/Worksheet/WorksheetDerivation'))
const ExamGroupScoring = lazy(() => import('./page/Lesson-prepare/PageExam/ExamGroupScoring'))
const CasGroupScoring = lazy(() => import('./page/group-scoring/Cas'))
const SignatureWorkbookViewer = lazy(() => import('./page/SignatureWorkbookViewer'))
const AcademyGroupWorkbookViewer = lazy(
  () => import('./page/SignatureWorkbookViewer/AcademyGroupWorkbookViewerPage'),
)

const PhoneAuth = lazy(() => import('./page/etc/PhoneAuth/PhoneAuth'))
const InitSetting = lazy(() => import('./page/etc/InitSetting/InitSetting'))

const 보고서생성수정 = lazy(() => import('./page/Lesson/Report/CreateUpdateReport'))
const LiveLessonBoard = lazy(() => import('./page/Lesson/Monitoring/LiveLesson/LiveLessonBoard'))
const LiveLessonPerson = lazy(() => import('./page/Lesson/Monitoring/LiveLesson/LiveLessonPerson'))
const CustomPrint = lazy(() => import('./page/CustomPrint/CustomPrint'))
const PaperEditTest = lazy(() => import('./page/DevTest/PaperEditTestPage'))
const HomePage = lazy(() => import('./page/Home/Home.page'))

const UpdatedContents = lazy(() => import('./page/etc/UpdatedContents/UpdatedContents'))
const GuestLoginPage = lazy(() => import('./page/etc/Login/GuestLogin/GuestLogin.page'))
const ScienceflatConnectPage = lazy(
  () => import('./page/etc/ScienceflatConnect/ScienceflatConnect.page'),
)
const AcademyInfoUpdate = lazy(() => import('./page/etc/AcademyInfoUpdate/AcademyInfoUpdate.page'))

const DailyLearningLiveLesson = lazy(
  () => import('./page/etc/DailyLearning/DailyLearningLiveLesson/DailyLearningLiveLesson'),
)
const MyDBEdit = lazy(() => import('./page/MyDBEdit'))

const OnlyViewClassDetail = lazy(
  () => import('src/page/Academy/AcademyManage/ClassManage/CreateUpdateClass/OnlyViewClassDetail'),
)
const SchoolExamZone = lazy(() => import('./page/etc/SchoolExamZone/SchoolExamZone'))
const ServerMaintenance = lazy(() => import('./page/etc/ServerMaintenance/ServerMaintenance'))
const ParentPaymentPage = lazy(() => import('./page/ParentPayment/ParentPayment.page'))
const ParentPaymentFailurePage = lazy(
  () => import('./page/ParentPayment/ParentPaymentFailure.page'),
)
const ParentPaymentSuccessPage = lazy(
  () => import('./page/ParentPayment/ParentPaymentSuccess.page'),
)
const PODMall = lazy(() => import('./page/PODMall/PODMall'))
const TallyEmbedPage = lazy(() => import('./page/etc/TallyEmbed/TallyEmbedPage'))

function App() {
  const history = useHistory()

  useEffect(() => {
    ServerMaintenanceService.checkServerMaintenance()
  }, []) // 서버 점검중인지 확인
  useSetStudentGroups() // 학생목록 최초 로드
  useVirtualStudent() // (무료체험 + 학생 0명) 가상 학생 로드
  useSavePath() // 경로 저장
  useInspectWorkbooks() // 검정교과서 최초 로드
  useDataTracking() // mixpanel 트래킹 시작
  const popupProps = usePopup(
    envUtils.isLive ||
      !!localStorageService.getItem('POPUP_VISIBILITY_DEV_ONLY') ||
      commonRepo.isSeoltab,
  ) // 팝업 로직
  useSSE() // SSE 알림 스트림 연결
  useToastCountManager()
  useBanner() // Banner 로드
  useAppUpdateDetection() // 앱 업데이트 감지
  useQuickMenuSetting() // 퀵메뉴 세팅
  // useSetVwoUserContext() // VWO Context 세팅

  useAppLastActiveTimeoutEffect({
    onTimeoutReturn: () => {
      if (commonRepo.isSeoltab || commonRepo.isSchoolflat) {
        return
      }
      history.push(RouteName.main)
    },
  })

  const appState = useLifeCycle()

  const isStagingOrLive = envUtils.isLive || envUtils.isStaging
  useDevToolbar({ enabled: !isStagingOrLive, popupProps })

  if (appState !== 'INITIALIZED') {
    return <></>
  }

  return (
    <>
      <GlobalStyle />
      <Suspense fallback={null}>
        <FloatingActionButtonWrap />
        <GlobalLoader />
        <GlobalFadeToast />
      </Suspense>
      {isStagingOrLive &&
        !SCREEN_SIZE_GUIDE_EXCLUDE_ROUTES.some((path) => {
          return matchPath(window.location.pathname, { path })
        }) && (
          <Suspense fallback={null}>
            <ScreenSizeGuide />
          </Suspense>
        )}

      <Suspense fallback={null}>
        <ErrorBoundary fallback={(reset) => <ErrorBoundaryFallback reset={reset} />}>
          <Switch>
            {/* dev test routes */}
            <PermissionGuardRoute path={RouteName.devPaperEditTest} component={PaperEditTest} />

            {/* public routes */}
            <PermissionGuardRoute path={RouteName.passwordReset} component={FindPassword} />
            <PermissionGuardRoute path={RouteName.phoneAuth} component={PhoneAuth} />
            <PermissionGuardRoute path={RouteName.supportWorkbook} component={SupportWorkbook} />
            <PermissionGuardRoute path={RouteName.login} component={Login} />
            <PermissionGuardRoute path={RouteName.busanLogin} component={Login} />
            <PermissionGuardRoute path={RouteName.jeonnamLogin} component={JeonnamLogin} />
            <PermissionGuardRoute path={RouteName.aiOnOAuthLogin} component={AiOnOAuthLogin} />
            <PermissionGuardRoute
              path={RouteName.aiOnOAuthCallback}
              component={AiOnOAuthCallbackPage}
            />
            <PermissionGuardRoute path={RouteName.aiepBridge} component={AiepBridgePage} />
            <PermissionGuardRoute path={RouteName.weizmannBridge} component={WeizmannLoginPage} />
            <PermissionGuardRoute
              path={RouteName.onlineOnboardingBridge}
              component={OnlineOnboardingLoginPage}
            />
            <PermissionGuardRoute path={RouteName.publicLogin} component={PublicLogin} />
            <PermissionGuardRoute
              path={RouteName.serverMaintenance}
              component={ServerMaintenance}
            />
            <PermissionGuardRoute
              exact
              path={RouteName.parentPayment}
              component={ParentPaymentPage}
            />
            <PermissionGuardRoute
              exact
              path={RouteName.parentPaymentSuccess}
              component={ParentPaymentSuccessPage}
            />
            <PermissionGuardRoute
              exact
              path={RouteName.parentPaymentFailure}
              component={ParentPaymentFailurePage}
            />
            <PermissionGuardRoute path={RouteName.guestLogin} component={GuestLoginPage} />
            <PermissionGuardRoute path={RouteName.seoltabLogin} component={GuestLoginPage} />
            <PermissionGuardRoute
              path={RouteName.scienceflatConnect}
              component={ScienceflatConnectPage}
            />

            {/* authenticated routes */}
            {/* full modal routes */}
            <PermissionGuardRoute path={RouteName.main} exact component={MainPage} />
            <PermissionGuardRoute path={RouteName.initSetting} component={InitSetting} />
            <PermissionGuardRoute path={RouteName.makeWorksheet} component={MakeWorksheet} />
            <PermissionGuardRoute path={RouteName.myDBEdit} component={MyDBEdit} />
            <PermissionGuardRoute
              path={RouteName.worksheetFileUpload}
              component={WorksheetFileUpload}
            />
            <PermissionGuardRoute
              path={RouteName.makeCustomWorkbook}
              component={MakeCustomWorkbook}
            />
            <PermissionGuardRoute path={RouteName.makeCas} component={MakeCas} />
            <PermissionGuardRoute
              path={RouteName.makeSignatureWorkbook}
              component={MakeSignatureWorkbook}
            />
            <PermissionGuardRoute
              path={RouteName.lessonWorksheetGroupScoring}
              component={WorksheetGroupScoring}
            />
            <PermissionGuardRoute
              path={RouteName.lessonWorksheetDerivation}
              component={WorksheetDerivation}
            />
            <PermissionGuardRoute path={RouteName.examGroupScoring} component={ExamGroupScoring} />
            <PermissionGuardRoute
              path={[RouteName.casGroupScoringByLessonClass, RouteName.casGroupScoringByGrade]}
              component={CasGroupScoring}
            />

            <PermissionGuardRoute
              path={[RouteName.createClass, RouteName.modifyClass]}
              component={
                commonRepo.permission.get.manage.class.등록
                  ? CreateUpdateClassManage
                  : OnlyViewClassDetail
              }
            />
            <PermissionGuardRoute path={RouteName.additionalUsage} component={추가요금내역} />
            <PermissionGuardRoute path={RouteName.smsUsage} component={문자요금내역} />
            <PermissionGuardRoute path={RouteName.hwpDownloadHistory} component={Hwp다운로드내역} />
            <PermissionGuardRoute
              exact
              path={[RouteName.createLessonReport, RouteName.updateLessonReport]}
              component={보고서생성수정}
            />
            <PermissionGuardRoute
              path={[
                RouteName.createLessonReportStepOne,
                RouteName.createLessonReportStepTwo,
                RouteName.updateLessonReportStepTwo,
                RouteName.createLessonReportFromRealTime,
              ]}
              component={보고서생성수정}
            />
            <PermissionGuardRoute
              path={[RouteName.signatureWorkbookViewerByStudent, RouteName.signatureWorkbookViewer]}
              component={SignatureWorkbookViewer}
            />
            <PermissionGuardRoute
              path={[
                RouteName.academyGroupWorkbookViewerByStudent,
                RouteName.academyGroupWorkbookViewer,
              ]}
              component={AcademyGroupWorkbookViewer}
            />
            <PermissionGuardRoute path={RouteName.podMall} component={PODMall} />
            <PermissionGuardRoute path={RouteName.liveLessonBoard} component={LiveLessonBoard} />
            <PermissionGuardRoute path={RouteName.liveLessonPerson} component={LiveLessonPerson} />
            <PermissionGuardRoute
              path={RouteName.lessonDailyLearningLiveLesson}
              component={DailyLearningLiveLesson}
            />
            <PermissionGuardRoute
              path={RouteName.lessonDailyLearningLiveLessonStudent}
              component={DailyLearningLiveLesson}
            />
            <PermissionGuardRoute path={RouteName.customPrint} component={CustomPrint} />
            <PermissionGuardRoute path={RouteName.tallyEmbed} exact component={TallyEmbedPage} />

            {/* default template routes */}
            <PermissionGuardRoute
              path={[
                ...(commonRepo.isSchoolflat ? [RouteName.home] : []),
                RouteName.lesson,
                RouteName.lessonPreparation,
                RouteName.academy,
                RouteName.myInfo,
                RouteName.schoolExamZone,
                RouteName.updatedContents,
                RouteName.updatedContentsById,
                RouteName.academyInfoUpdate,
              ]}
            >
              <DefaultPageTemplate>
                <Suspense fallback={<PageContentTemplate.Fallback />}>
                  <Switch>
                    {commonRepo.isSchoolflat && (
                      <PermissionGuardRoute path={RouteName.home} component={HomePage} />
                    )}
                    <PermissionGuardRoute path={RouteName.academy} component={Academy} />
                    <PermissionGuardRoute path={RouteName.lesson} component={Lesson} />
                    <PermissionGuardRoute
                      path={RouteName.lessonPreparation}
                      component={LessonPreparation}
                    />
                    <PermissionGuardRoute path={RouteName.myInfo} component={MyPage} />
                    <PermissionGuardRoute
                      path={RouteName.schoolExamZone}
                      component={SchoolExamZone}
                    />
                    <PermissionGuardRoute
                      path={RouteName.updatedContentsById}
                      component={UpdatedContents}
                    />
                    <PermissionGuardRoute
                      path={RouteName.updatedContents}
                      component={UpdatedContents}
                    />
                    <PermissionGuardRoute
                      path={RouteName.academyInfoUpdate}
                      component={AcademyInfoUpdate}
                    />
                  </Switch>
                </Suspense>
              </DefaultPageTemplate>
            </PermissionGuardRoute>

            <Redirect to={RouteName.login} />
          </Switch>
        </ErrorBoundary>
      </Suspense>
    </>
  )
}
export default observer(App)
